import React, { useEffect, useState } from 'react'
import { AppContainer } from '@connectcore/connect-components-react'
import { CallbackType, FRAuth, StepType } from '@forgerock/javascript-sdk'
import { Route, Switch, useHistory, Redirect } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import {
  DEV_ENV,
  ENROLL_ROUTE,
  ENVIRONMENT_VARIABLES,
  FORGOT_PASSWORD_ROUTE,
  FORGOT_USERNAME_ROUTE,
  SCD_DISCLAIMER,
  LOGIN_ROUTE,
  PROD_ENV,
  SELF_ENROLL_URL,
  SESSION_EXPIRY_ROUTE,
  SIT_ENV,
  TEXT_OUTPUT_CALLBACK,
  UAT_ENV,
} from '~utils/constants'
import { BRAND_IMAGE_ORIENTATION, SHOW_FOOTER, SHOW_HEADER, THEME } from '~utils/featureFlags'
import LoginJourney from '~journeys/LoginJourney/LoginJourney'
import ForgotPasswordJourney from '~journeys/ForgotPasswordJourney/ForgotPasswordJourney'
import ForgotUsernameJourney from '~journeys/ForgotUsernameJourney/ForgotUsernameJourney'
import Header from '~components/Header/Header'
import Footer from '~components/Footer/Footer'
import InfoSection from '~components/InfoSection/InfoSection'
import { UserContext } from '~utils/context/UserContext'
import sessionExpire from '~components/SessionExpire/SessionExpire'
import { checkPingTokenExpiry, decodeToken } from '~utils/forgerock'
import favicon from '~images/favicon.png'
import * as api from '~utils/api'

import '~styles/common.scss'

const MainView = () => {
  const [step, setStep] = useState<any>(undefined)
  const [showErr, setShowErr] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [storeToken, setStoreToken] = useState('')
  const [frSessionTimeout, setfrSessionTimeout] = useState(false)
  const [environment, setEnvironment] = useState('')
  const [userInputError, setUserInputError] = useState('')
  const [selectedOTPChannel, setSelectedOTPChannel] = useState(null)
  const [pingTokenTimeout, setPingTokenTimeout] = useState(false)
  const [otpSupport, setOtpSupport] = useState(false)
  const urlParams = new URLSearchParams(window.location.search)
  const paramToken: any = urlParams.get('params')
  const history = useHistory()

  useEffect(() => {
    if (paramToken && decodeToken(paramToken)) {
      setStoreToken(paramToken)
    }
  }, [paramToken])

  useEffect(() => {
    getEnvironmentDetails()
  }, [])

  useEffect(() => {
    if (!storeToken && !paramToken) {
      setPingTokenTimeout(true)
      setIsLoading(false)
      history.push(SESSION_EXPIRY_ROUTE)
    } else {
      setPingTokenTimeout(false)
    }
  }, [storeToken, paramToken])

  const getEnvironmentDetails = async () => {
    try {
      const response = await api.getEnv()
      const data = await response.json()
      if (Array.isArray(data)) {
        const environmentDetails =
          data?.includes(SIT_ENV) || data?.includes(DEV_ENV)
            ? SIT_ENV
            : data?.includes(UAT_ENV)
            ? UAT_ENV
            : data?.includes(PROD_ENV)
            ? PROD_ENV
            : SIT_ENV
        setEnvironment(environmentDetails)
      }
    } catch (e) {
      // TODO: update to Unknown Error for local environment and  if /env does not return value
      setEnvironment(SIT_ENV)
    }
  }

  const getNextStep = async step => {
    try {
      setIsLoading(true)
      if (checkPingTokenExpiry()) {
        setPingTokenTimeout(true)
      } else {
        setPingTokenTimeout(false)
      }
      setUserInputError('')
      const nextStep = await FRAuth.next(step)
      setIsLoading(false)
      if (nextStep.type === StepType.LoginFailure) {
        if (nextStep?.payload?.message === 'Session has timed out') {
          setfrSessionTimeout(true)
        } else {
          setfrSessionTimeout(false)
        }
        setShowErr(true)
      } else {
        const callBackStep: any = nextStep
        if (
          callBackStep?.type === StepType.Step &&
          callBackStep?.getCallbacksOfType(CallbackType.TextOutputCallback)?.length > 0
        ) {
          const outputCallBack = callBackStep?.getCallbackOfType(CallbackType.TextOutputCallback)
          if (outputCallBack?.getMessageType() === TEXT_OUTPUT_CALLBACK.ERROR) {
            setUserInputError(outputCallBack.getMessage())
          }
        } else {
          setStep(nextStep)
          setUserInputError('')
        }

        setShowErr(false)
        setfrSessionTimeout(false)
      }
    } catch (err) {
      setIsLoading(false)
      setStep(undefined)
    }
  }

  return (
    <AppContainer className="App_Container" data-testid="MainView" canvas>
      <Helmet>
        <html className={THEME} />
        <link href={favicon} rel="shortcut icon" />
      </Helmet>
      {SHOW_HEADER && <Header />}
      <main className={`row ${BRAND_IMAGE_ORIENTATION === 'right' ? 'right-image-row' : ''}`}>
        {BRAND_IMAGE_ORIENTATION === 'left' && <InfoSection />}
        <div
          className={`MainForm col-12 col-md-6 ${
            BRAND_IMAGE_ORIENTATION === 'none' ? 'no-image-container' : ''
          }`}
        >
          <div className="d-flex flex-column h-95vh">
            <div className="scrollbarClass d-flex flex-column">
              <UserContext.Provider
                value={{
                  step: step,
                  nextStep: getNextStep,
                  showErr: showErr,
                  isLoading: isLoading,
                  storeToken: storeToken,
                  frSessionTimeout: frSessionTimeout,
                  setStep: setStep,
                  setIsLoading: setIsLoading,
                  setStoreToken: setStoreToken,
                  env: environment,
                  userInputError: userInputError,
                  selectedOTPChannel: selectedOTPChannel,
                  setSelectedOTPChannel: setSelectedOTPChannel,
                  otpSupport: otpSupport,
                  setOtpSupport: setOtpSupport,
                  pingTokenTimeout: pingTokenTimeout,
                }}
              >
                <Switch>
                  <Route exact path={LOGIN_ROUTE} component={LoginJourney} />
                  <Route exact path={FORGOT_PASSWORD_ROUTE} component={ForgotPasswordJourney} />
                  <Route exact path={FORGOT_USERNAME_ROUTE} component={ForgotUsernameJourney} />
                  <Route
                    exact
                    path={ENROLL_ROUTE}
                    component={() => {
                      if (!environment || !ENVIRONMENT_VARIABLES[environment]) {
                        return <Redirect to={LOGIN_ROUTE} />
                      }
                      window.open(ENVIRONMENT_VARIABLES[environment][SELF_ENROLL_URL], '_blank')
                      return <Redirect to={LOGIN_ROUTE} />
                    }}
                  />
                  <Route exact path={SESSION_EXPIRY_ROUTE} component={sessionExpire} />
                  <Route path="*" component={LoginJourney} />
                </Switch>
              </UserContext.Provider>
            </div>
            <p className="mt-auto disclaimer w-70">
              <br />
              {SCD_DISCLAIMER}
            </p>
          </div>
        </div>
        {BRAND_IMAGE_ORIENTATION === 'right' && <InfoSection />}
      </main>
      {SHOW_FOOTER && <Footer />}
    </AppContainer>
  )
}

export default MainView
